import { ANALYTICS_URL, CACHE_KEY, INDEX_URLS, PUSHES_KEY, PUSHES_TTL_MS, REPOS_URL } from '../config';
import type { AnalyticsFile, Entry, RawIndexEntry, RepoMeta, RepoPush } from '../types';
import { dedupeEntries, filterBlocked, normalizeEntry } from './merge';

export interface LoadResult {
  entries: Entry[];
  /** Human-readable labels of sources that failed to load. */
  failures: string[];
  fromCache: boolean;
}

async function fetchIndex(url: string, key: string): Promise<RawIndexEntry[]> {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`${key}: HTTP ${res.status}`);
  const data = await res.json();
  const list = data?.[key];
  if (!Array.isArray(list)) throw new Error(`${key}: malformed index`);
  return list;
}

function readCache(): Entry[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? (parsed as Entry[]) : null;
  } catch {
    return null;
  }
}

function writeCache(entries: Entry[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entries));
  } catch {
    // storage full/denied — cache is best-effort
  }
}

/** Fetch both factory indexes; each fails independently. Falls back to a cached copy when everything is down. */
export async function loadIndexes(): Promise<LoadResult> {
  const results = await Promise.allSettled(INDEX_URLS.map(({ url, key }) => fetchIndex(url, key)));
  const failures: string[] = [];
  const raw: RawIndexEntry[] = [];
  results.forEach((r, i) => {
    if (r.status === 'fulfilled') raw.push(...r.value);
    else failures.push(INDEX_URLS[i].key);
  });
  if (raw.length === 0) {
    const cached = readCache();
    // Re-filter the cache: a copy written before a product was blocked could
    // otherwise resurrect it.
    if (cached) return { entries: filterBlocked(cached), failures, fromCache: true };
    return { entries: [], failures, fromCache: false };
  }
  const entries = filterBlocked(
    dedupeEntries(raw.map(normalizeEntry).filter((e): e is Entry => e !== null)),
  );
  if (failures.length === 0) writeCache(entries);
  return { entries, failures, fromCache: false };
}

function readPushCache(): RepoPush[] | null {
  try {
    const raw = localStorage.getItem(PUSHES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; pushes: RepoPush[] };
    if (!parsed || typeof parsed.at !== 'number' || !Array.isArray(parsed.pushes)) return null;
    if (Date.now() - parsed.at > PUSHES_TTL_MS) return null;
    return parsed.pushes;
  } catch {
    return null;
  }
}

function writePushCache(pushes: RepoPush[]): void {
  try {
    localStorage.setItem(PUSHES_KEY, JSON.stringify({ at: Date.now(), pushes }));
  } catch {
    // storage full/denied — cache is best-effort
  }
}

/**
 * Push + creation times for every ben-gy repo, keyed by lowercased "owner/name".
 * Powers the UPDATED badge (pushedAt) and the NEW badge (createdAt). Best-effort:
 * a rate-limited or offline fetch just yields an empty map (no badges) rather
 * than blocking the page. Cached briefly to keep reloads off the anonymous
 * GitHub rate limit.
 */
export async function loadRepoPushes(): Promise<Map<string, RepoMeta>> {
  const cached = readPushCache();
  const pushes = cached ?? (await fetchRepoPushes());
  if (pushes && !cached) writePushCache(pushes);
  return new Map(
    (pushes ?? []).map((p) => [p.repo, { pushedAt: p.pushedAt, createdAt: p.createdAt ?? null }]),
  );
}

async function fetchRepoPushes(): Promise<RepoPush[] | null> {
  try {
    const res = await fetch(REPOS_URL, {
      cache: 'no-cache',
      headers: { Accept: 'application/vnd.github+json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data)) return null;
    return data
      .filter((r) => r && typeof r.full_name === 'string' && typeof r.pushed_at === 'string')
      .map((r) => ({
        repo: r.full_name.toLowerCase(),
        pushedAt: r.pushed_at as string,
        createdAt: typeof r.created_at === 'string' ? (r.created_at as string) : null,
      }));
  } catch {
    return null;
  }
}

/** Fetch the nightly analytics file; returns null on any failure (a designed state early on). */
export async function loadAnalytics(): Promise<AnalyticsFile | null> {
  try {
    const res = await fetch(ANALYTICS_URL, { cache: 'no-cache' });
    if (!res.ok) return null;
    const type = res.headers.get('content-type') ?? '';
    if (!type.includes('json')) return null;
    const data = (await res.json()) as AnalyticsFile;
    if (!data || data.version !== 1 || typeof data.hosts !== 'object') return null;
    return data;
  } catch {
    return null;
  }
}
